import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCategories } from '../services/api';

function MobileMenuModal({ isOpen, onClose, activeFilter, onFilterChange, onSearchClear, onCategoryFilterChange }) {
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Fetch categories when categories submenu opens
  useEffect(() => {
    if (categoriesOpen && categories.length === 0) {
      const fetchCategories = async () => {
        try {
          setCategoriesLoading(true);
          const data = await getCategories();
          setCategories(data);
        } catch (error) {
          console.error('Error fetching categories:', error);
        } finally {
          setCategoriesLoading(false);
        }
      };
      fetchCategories();
    }
  }, [categoriesOpen, categories.length]);

  // Reset categories open state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCategoriesOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCategoryClick = (categoryId) => {
    if (onCategoryFilterChange) {
      onCategoryFilterChange(categoryId);
    }
    if (onSearchClear) {
      onSearchClear();
    }
    setCategoriesOpen(false);
    onClose();
  };

  const menuItems = [
    {
      filter: 'best-selling',
      label: 'Best-Selling Items',
      icon: (
        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
        </svg>
      ),
    },
    {
      filter: '5-star',
      label: '5-Star Rated',
      icon: (
        <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
    },
    {
      type: 'categories',
      label: 'Categories',
      icon: (
        <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      href: '/support',
      label: 'Support',
      icon: (
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      to: '/orders',
      label: 'Orders',
      icon: (
        <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] transition-opacity duration-300 animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[36px] sm:pt-[40px] px-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm transform transition-all duration-300 ease-out pointer-events-auto animate-modal-pop-top"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-800">Menu</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5 text-gray-500 hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" className="opacity-20"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 9l-6 6M9 9l6 6" />
              </svg>
            </button>
          </div>

          {/* Menu Items */}
          <div className="p-5 max-h-[70vh] overflow-y-auto">
            <nav className="flex flex-col gap-2">
              {menuItems.map((item, index) => {
                // Filter items (Best-Selling, 5-Star)
                if (item.filter) {
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        if (onFilterChange) {
                          onFilterChange(activeFilter === item.filter ? null : item.filter);
                        }
                        if (onSearchClear) {
                          onSearchClear();
                        }
                        onClose();
                      }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm border ${
                        activeFilter === item.filter
                          ? 'bg-primary/10 text-primary border-primary/30 shadow-sm'
                          : 'text-secondary border-gray-100 hover:bg-primary/10 hover:text-primary hover:border-primary/30 hover:shadow-sm'
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  );
                }
                
                // Categories - Expandable submenu
                if (item.type === 'categories') {
                  return (
                    <div key={index} className="flex flex-col gap-2">
                      <button
                        onClick={() => setCategoriesOpen(!categoriesOpen)}
                        className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm border ${
                          categoriesOpen || (activeFilter && activeFilter.startsWith('category-'))
                            ? 'bg-primary/10 text-primary border-primary/30 shadow-sm'
                            : 'text-secondary border-gray-100 hover:bg-primary/10 hover:text-primary hover:border-primary/30 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {item.icon}
                          <span>{item.label}</span>
                        </div>
                        <svg 
                          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${categoriesOpen ? 'rotate-180' : ''}`}
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* Categories Submenu */}
                      {categoriesOpen && (
                        <div className="ml-4 pl-4 border-l-2 border-gray-200 flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto">
                          {categoriesLoading ? (
                            <div className="px-4 py-2 text-xs text-gray-500">Loading categories...</div>
                          ) : categories.length === 0 ? (
                            <div className="px-4 py-2 text-xs text-gray-500">No categories available</div>
                          ) : (
                            categories.map((category) => (
                              <button
                                key={category.id}
                                onClick={() => handleCategoryClick(category.id)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-xs font-medium text-left ${
                                  activeFilter === `category-${category.id}`
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <span>{category.name}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
                
                // Regular navigation items (Support, Orders, etc.)
                if (item.to) {
                  return (
                    <Link
                      key={index}
                      to={item.to}
                      onClick={onClose}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-semibold text-sm border text-secondary border-gray-100 hover:bg-primary/10 hover:text-primary hover:border-primary/30 hover:shadow-sm"
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  );
                }
                return (
                  <a
                    key={index}
                    href={item.href || '#'}
                    onClick={onClose}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-primary/10 hover:text-primary transition-all duration-200 text-secondary font-semibold text-sm border border-gray-100 hover:border-primary/30 hover:shadow-sm"
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </a>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}

export default MobileMenuModal;

